// /login — magic-link + passkey sign-in against the local better-auth
// (/api/auth/*, src/server/auth.mjs). Email + Turnstile gate the link
// send ; the passkey path is one click for already-enrolled users.

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Turnstile from '../components/Turnstile.jsx';
import { signIn } from '../auth/client.js';
import { classifyPasskeyError } from '../auth/passkey-errors.js';
import { Button } from '@/components/ui/Button.jsx';
import { inputClass, labelClass } from '@/components/ui/field-classes.js';

// Turnstile is only enforced when a site key is configured. Local dev has
// none, so the anti-bot gate is skipped there (the server's
// /api/turnstile/verify likewise no-ops without a secret) — otherwise the
// magic-link button could never be enabled. In staging/prod the key is set
// and the gate is active.
const TURNSTILE_ENABLED = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);

export default function Login() {
  const { t } = useTranslation(['auth', 'common']);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState(null);
  const autofillStarted = useRef(false);
  // GitHub App install round-trip (SEC-002, mission gwt-ma-github-app-foundation) :
  // the install callback redirects here with ?next=<encoded callback path>
  // when the browser had no session — RequireAuth's own `from` (router
  // STATE, not a query param) covers every other protected route, but a
  // fresh top-level navigation from the callback carries no client-side
  // router state, only the query string. Only ever trusted when it matches
  // the exact callback path (never an arbitrary open redirect).
  const nextParam = new URLSearchParams(location.search).get('next');
  const isInstallCallbackNext = typeof nextParam === 'string' && nextParam.startsWith('/api/github/install/callback?');
  // '/' lets the index route resolve the right office dashboard (or
  // onboarding) from /api/me after the session lands.
  const callbackURL = isInstallCallbackNext ? nextParam : (location.state?.from || '/');

  // The install callback is a SERVER route, not a client-side one — a plain
  // client-side navigate() would render the SPA's 404 instead of hitting it.
  function goToCallback(url) {
    if (isInstallCallbackNext) {
      window.location.assign(url);
      return;
    }
    navigate(url, { replace: true });
  }

  // A passkey sign-in sets the session cookie without a page load (unlike the
  // magic link, which lands on a fresh navigation). Drop the cached session so
  // RequireAuth re-reads it instead of bouncing on a cached anonymous result.
  function landAfterPasskey() {
    queryClient.resetQueries({ queryKey: ['session'] });
    goToCallback(callbackURL);
  }

  // Conditional UI : the browser offers saved passkeys in the email field's
  // autofill. Errors are silent — aborts are routine (user typed an email, or
  // the button started its own ceremony).
  useEffect(() => {
    if (autofillStarted.current) return;
    autofillStarted.current = true;
    const check = window.PublicKeyCredential?.isConditionalMediationAvailable;
    if (typeof check !== 'function') return;
    (async () => {
      try {
        if (!(await window.PublicKeyCredential.isConditionalMediationAvailable())) return;
        const { data } = await signIn.passkey({ autoFill: true });
        if (data) landAfterPasskey();
      } catch {
        // silent
      }
    })();
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (TURNSTILE_ENABLED && !token) {
      setError(t('login.turnstile.failed'));
      return;
    }
    setSubmitting(true);
    try {
      // Server verifies the Turnstile token (POST /api/turnstile/verify) —
      // only when Turnstile is configured.
      if (TURNSTILE_ENABLED) {
        const verify = await fetch(`/api/turnstile/verify`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });
        if (!verify.ok) {
          setError(t('login.turnstile.failed'));
          return;
        }
      }

      const { error: sendError } = await signIn.magicLink({ email, callbackURL });
      if (sendError) {
        setError(t('common:error.generic'));
        return;
      }
      setMessage(t('login.success'));
    } catch {
      setError(t('common:error.network'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onPasskey() {
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const { error: pkError } = await signIn.passkey();
      if (pkError) {
        const kind = classifyPasskeyError(pkError);
        if (kind === 'cancelled') setNotice(t('login.passkey.cancelled'));
        else if (kind === 'notFound') setError(t('login.passkey.notFound'));
        else setError(t('login.passkey.failed'));
        return;
      }
      landAfterPasskey();
    } catch {
      setError(t('common:error.network'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      aria-labelledby="login-title"
      className="flex flex-1 items-center justify-center px-6 py-12"
    >
      <div className="w-full max-w-sm rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm sm:p-8">
        <h1 id="login-title" className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          {t('login.title')}
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('common:app.name')}</p>

        <form onSubmit={onSubmit} noValidate className="mt-6 space-y-4">
          <div>
            <label htmlFor="email" className={labelClass}>{t('login.email.label')}</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email webauthn"
              required
              placeholder={t('login.email.placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </div>

          {TURNSTILE_ENABLED && (
            <Turnstile
              onVerify={setToken}
              onExpire={() => setToken('')}
              onError={() => setToken('')}
            />
          )}

          <Button
            type="submit"
            disabled={submitting || !email || (TURNSTILE_ENABLED && !token)}
            className="w-full"
          >
            {submitting ? t('login.submitting') : t('login.submit')}
          </Button>

          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
            <span className="text-xs text-slate-400 dark:text-slate-500">{t('login.or', 'ou')}</span>
            <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
          </div>

          <Button type="button" variant="secondary" onClick={onPasskey} disabled={submitting} className="w-full">
            {t('login.passkey', 'Use a passkey')}
          </Button>

          {error && (
            <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}
          {notice && (
            <p role="status" className="text-sm text-slate-500 dark:text-slate-400">
              {notice}
            </p>
          )}
          {message && (
            <p role="status" className="rounded-md bg-green-50 dark:bg-green-500/15 px-3 py-2 text-sm text-green-700 dark:text-green-300">
              {message}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
